import { BusFront, LoaderCircle, ShieldCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { AuthConfig } from "./api";

interface CredentialResponse { credential?: string }
interface GoogleAccounts {
  id: {
    initialize(options: { client_id: string; callback: (response: CredentialResponse) => void; auto_select?: boolean; cancel_on_tap_outside?: boolean }): void;
    renderButton(element: HTMLElement, options: Record<string, unknown>): void;
  };
}

declare global { interface Window { google?: { accounts: GoogleAccounts } } }

interface GoogleLoginProps {
  config?: AuthConfig;
  loading: boolean;
  error?: string | null;
  onCredential: (credential: string) => Promise<void>;
}

export function GoogleLogin({ config, loading, error, onCredential }: GoogleLoginProps) {
  const button = useRef<HTMLDivElement>(null);
  const [scriptReady, setScriptReady] = useState(Boolean(window.google));
  useEffect(() => {
    if (window.google) { setScriptReady(true); return; }
    const existing = document.querySelector<HTMLScriptElement>('script[data-google-identity="true"]');
    const script = existing ?? Object.assign(document.createElement("script"), { src: "https://accounts.google.com/gsi/client?hl=zh-TW", async: true, defer: true });
    script.dataset.googleIdentity = "true";
    const ready = () => setScriptReady(true);
    script.addEventListener("load", ready);
    if (!existing) document.head.appendChild(script);
    return () => script.removeEventListener("load", ready);
  }, []);
  useEffect(() => {
    if (!scriptReady || !config?.googleClientId || !button.current || !window.google) return;
    button.current.replaceChildren();
    window.google.accounts.id.initialize({ client_id: config.googleClientId, callback: (response) => { if (response.credential) void onCredential(response.credential); }, auto_select: false, cancel_on_tap_outside: true });
    window.google.accounts.id.renderButton(button.current, { type: "standard", theme: "filled_black", size: "large", shape: "pill", text: "continue_with", logo_alignment: "left", width: Math.min(320, window.innerWidth - 64), locale: "zh-TW" });
  }, [scriptReady, config?.googleClientId, onCredential]);

  return <main className="login-shell">
    <section className="login-card" aria-labelledby="login-title">
      <div className="login-logo"><BusFront size={32} /></div>
      <small>BUS NOW · TAIPEI</small>
      <h1 id="login-title">你的公車通勤，準時開始</h1>
      <p>登入後同步常用路線、方向與到站提醒，每台裝置仍可獨立控制通知。</p>
      <div className="login-benefits"><span><ShieldCheck size={17} />只使用 Google 帳號基本身份</span><span><ShieldCheck size={17} />不會取得 Google 密碼</span></div>
      {config?.googleClientId ? <div className="google-login-button" ref={button} aria-label="使用 Google 帳號登入" /> : <div className="login-unavailable">Google Web Client ID 尚未設定</div>}
      {loading && <div className="login-progress" role="status"><LoaderCircle size={17} />登入驗證中…</div>}
      {error && <div className="login-error" role="alert">{error}</div>}
      <footer>登入即表示你同意本站以必要 Cookie 維持登入狀態。</footer>
    </section>
  </main>;
}
